import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PALETTE } from '../tokens';
import { useAuth } from '../context/AuthContext';
import { rciApi } from '../api/rci';
import { insightsApi } from '../api/insights';
import { presentedScore } from '../utils/presentedScore';
import { coherenceLabel } from '../utils/coherenceLabels';
import { daySignalLabel } from '../utils/dailySignalLabel';
import { BottomNav } from '../components/BottomNav';
import SignalInfoButton from '../components/SignalInfoButton';
import { Wordmark } from '../components/Wordmark';
import { DOMAIN_META } from '../utils/domainLabels';

/**
 * SignalScreen — the daily habit surface at /signal.
 *
 * Distinct from CoherenceDashboard (the reused 7-day reveal at /coherence):
 * this is the every-day landing for the Signal nav button. Same data
 * sources (rciApi.snapshot/history + insightsApi.get), purpose-built layout:
 * a radial 270° dial up top, then a daily read, domain rows, and the
 * tap-throughs to patterns / chat / practitioner report / history.
 *
 * Palette is the app's warm-light tokens — gold arc on a dim warm track,
 * reading on cream. No new colors or fonts.
 */

// Reused from BottomNav.jsx — the waveform Signal mark.
const WAVEFORM = 'M22 12h-4l-3 9L9 3l-3 9H2';

// Radial dial geometry: 270° sweep with a 90° gap centered at the bottom.
const DIAL_R = 80;
const DIAL_C = 2 * Math.PI * DIAL_R;
const ARC_FRACTION = 0.75; // 270° of the full circle
const TRACK_DASH = `${ARC_FRACTION * DIAL_C} ${(1 - ARC_FRACTION) * DIAL_C}`;

// One-word domain state from a 0.0–1.0 score, matching the domain
// thresholds (≥0.75 positive, 0.50–0.74 neutral, <0.50 negative).
function domainWord(score) {
  if (score == null) return '—';
  if (score >= 0.75) return 'Strong';
  if (score >= 0.5) return 'Steady';
  return 'Low';
}

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .sig-root {
    font-family: 'DM Sans', sans-serif;
    background: ${PALETTE.bg};
    /* dvh tracks Safari's collapsing toolbar; vh is the pre-iOS-15.4
       fallback and is declared first so old engines keep the old value. */
    min-height: 100vh;
    min-height: 100dvh;
    color: ${PALETTE.text};
    max-width: 420px;
    margin: 0 auto;
  }
  .serif { font-family: 'Playfair Display', Georgia, serif; }

  @keyframes sigFadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes sigPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
  .sig-fu { animation: sigFadeUp 0.5s ease forwards; }
  .sig-d1 { animation-delay: 0.05s; opacity: 0; }
  .sig-d2 { animation-delay: 0.15s; opacity: 0; }
  .sig-d3 { animation-delay: 0.25s; opacity: 0; }
  .sig-d4 { animation-delay: 0.35s; opacity: 0; }
  .sig-d5 { animation-delay: 0.45s; opacity: 0; }

  .sig-tap {
    width: 100%;
    text-align: left;
    background: ${PALETTE.surface};
    border: none;
    border-radius: 18px;
    padding: 18px 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    box-shadow: 0 1px 16px rgba(28,24,20,0.06);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }
  .sig-tap:hover { transform: translateY(-1px); box-shadow: 0 4px 22px rgba(28,24,20,0.10); }
`;

export default function SignalScreen() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [explainerOpen, setExplainerOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      rciApi.snapshot(user.id, token).catch(() => null),
      rciApi.history(user.id, 30, token).catch(() => []),
      insightsApi.get(user.id, token).catch(() => null),
    ])
      .then(([snap, hist, ins]) => {
        setSnapshot(snap);
        setHistory(hist || []);
        setInsights(ins);
      })
      .finally(() => setLoading(false));
  }, [user, token]);

  // chronological = oldest → newest (history arrives newest-first).
  const chronological = [...(history || [])].reverse();

  const coherenceStatus = snapshot?.coherence_status || 'CALIBRATING';
  const daysInApp = snapshot?.total_snapshots || 0;
  const score = snapshot ? presentedScore(snapshot) : null;
  const calibrating =
    score === null || daysInApp < 7 || coherenceStatus === 'CALIBRATING';

  // Delta vs. yesterday — derived from history exactly as the dashboard does.
  const previousScore =
    chronological.length >= 2
      ? presentedScore(chronological[chronological.length - 2])
      : null;
  const delta =
    !calibrating && previousScore !== null ? score - previousScore : null;

  // "Today's read" — prefer the AI insight, fall back to the computed label.
  const todaysRead =
    insights?.current_insight ||
    (chronological.length
      ? daySignalLabel(chronological.length - 1, chronological)
      : '');

  const stateLabel = coherenceLabel(coherenceStatus, { fallback: 'Calibrating' });

  const domains = DOMAIN_META.map((d) => ({
    display: d.display,
    score: snapshot?.[`${d.key}_score`] ?? null,
  }));

  const arcFraction = calibrating
    ? 0
    : Math.max(0, Math.min(1, score / 100));
  const goldDash = `${arcFraction * ARC_FRACTION * DIAL_C} ${DIAL_C}`;

  return (
    <>
      <style>{STYLES}</style>
      <div className="sig-root">
        {/* warm wash behind the dial */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 420,
            height: 360,
            pointerEvents: 'none',
            zIndex: 0,
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(158,132,86,0.10) 0%, transparent 70%)',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1, paddingBottom: 'var(--nav-clearance)' }}>
          {/* Header */}
          <div
            className="sig-fu sig-d1"
            style={{ padding: 'max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <Wordmark />
              <div style={{ fontSize: 13, color: PALETTE.textMute, fontWeight: 300 }}>
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
            </div>
            {daysInApp > 0 && (
              <div
                style={{
                  fontSize: 10,
                  color: PALETTE.primary,
                  border: `1px solid ${PALETTE.border}`,
                  padding: '4px 11px',
                  borderRadius: 99,
                  letterSpacing: '0.07em',
                  fontWeight: 500,
                }}
              >
                Day {daysInApp}
              </div>
            )}
          </div>

          {/* Radial dial */}
          <div className="sig-fu sig-d2" style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
            <Dial
              score={score}
              calibrating={calibrating}
              goldDash={goldDash}
              delta={delta}
              stateLabel={stateLabel}
              daysInApp={daysInApp}
              loading={loading}
            />
          </div>

          {/* Score basis disclosure (Guideline 1.4.1) */}
          <div className="sig-fu sig-d2" style={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
            <SignalInfoButton />
          </div>

          <div style={{ padding: '0 24px' }}>
            {/* Today's read */}
            {todaysRead && (
              <div className="sig-fu sig-d3" style={{ marginTop: 8, textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: 9.5,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: PALETTE.primaryMid,
                    fontWeight: 500,
                    marginBottom: 8,
                  }}
                >
                  Today's read
                </div>
                <p
                  className="serif"
                  style={{
                    fontSize: 18,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: PALETTE.text,
                    lineHeight: 1.5,
                    maxWidth: 320,
                    margin: '0 auto',
                  }}
                >
                  {todaysRead}
                </p>
              </div>
            )}

            {/* Body / Mind / Behavior rows */}
            <div
              className="sig-fu sig-d3"
              style={{
                marginTop: 28,
                background: PALETTE.surface,
                borderRadius: 20,
                padding: '6px 22px',
                boxShadow: '0 1px 16px rgba(28,24,20,0.06)',
              }}
            >
              {domains.map((d, i) => (
                <div key={d.display}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 0',
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 500, color: PALETTE.text }}>{d.display}</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                      <span
                        className="serif"
                        style={{ fontSize: 22, fontStyle: 'italic', color: PALETTE.text, lineHeight: 1 }}
                      >
                        {calibrating || d.score == null ? '—' : Math.round(d.score * 100)}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          fontWeight: 500,
                          color: PALETTE.primaryMid,
                          minWidth: 52,
                          textAlign: 'right',
                        }}
                      >
                        {calibrating ? 'Forming' : domainWord(d.score)}
                      </span>
                    </div>
                  </div>
                  {i < domains.length - 1 && <div style={{ height: 1, background: PALETTE.surfaceAlt }} />}
                </div>
              ))}
            </div>

            {/* Plain-language explainer — teaches the three domains + coherence */}
            <div className="sig-fu sig-d3" style={{ marginTop: 14 }}>
              <button
                onClick={() => setExplainerOpen((o) => !o)}
                aria-expanded={explainerOpen}
                style={{
                  width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '6px 0', color: PALETTE.primary, fontSize: 12.5, fontWeight: 500, letterSpacing: '0.02em',
                }}
              >
                What is my Signal?
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={PALETTE.primary}
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: explainerOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {explainerOpen && (
                <div style={{ marginTop: 8, background: PALETTE.surface, borderRadius: 16, padding: '16px 18px', boxShadow: '0 1px 16px rgba(28,24,20,0.06)' }}>
                  <p style={{ fontSize: 13, color: PALETTE.textMid, lineHeight: 1.72, fontWeight: 300 }}>
                    Your Signal blends three domains —{' '}
                    <strong style={{ fontWeight: 500, color: PALETTE.text }}>Body</strong> (how rested you feel),{' '}
                    <strong style={{ fontWeight: 500, color: PALETTE.text }}>Mind</strong> (how clear and steady you feel), and{' '}
                    <strong style={{ fontWeight: 500, color: PALETTE.text }}>Behavior</strong> (how your actions line up with your intentions).
                    When the three move in concert, that&rsquo;s{' '}
                    <em style={{ fontStyle: 'italic', color: PALETTE.text }}>coherence</em> — running as one system instead of pulling in different directions.
                  </p>
                </div>
              )}
            </div>

            {/* Patterns */}
            <button
              className="sig-tap sig-fu sig-d4"
              style={{ marginTop: 14 }}
              onClick={() => navigate('/intelligence')}
            >
              <span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: PALETTE.text }}>
                  What patterns is your data showing?
                </span>
                <span style={{ display: 'block', fontSize: 12, color: PALETTE.textMid, fontWeight: 300, marginTop: 3 }}>
                  See what Haiven has noticed this week
                </span>
              </span>
              <Chevron />
            </button>

            {/* Chat — soft gold mid-tone in the light→dark gradient */}
            <button
              className="sig-tap sig-fu sig-d4"
              style={{ marginTop: 12, background: 'rgba(158,132,86,0.55)' }}
              onClick={() =>
                navigate('/chat', {
                  state: { seedMessage: "I'm feeling a little off today and want to talk it through." },
                })
              }
            >
              <span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: PALETTE.text }}>
                  Feeling off? Chat with Haiven
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'rgba(28,24,20,0.58)', fontWeight: 300, marginTop: 3 }}>
                  Talk through what's happening right now
                </span>
              </span>
              <ChatIcon color={PALETTE.text} />
            </button>

            {/* Practitioner report — dark anchor of the light→dark gradient.
                Routes to the in-app preview (preview-first: see the report
                before minting any link; the share modal lives there). */}
            <button
              className="sig-tap sig-fu sig-d5"
              style={{ marginTop: 12, background: 'rgba(28,24,20,0.64)' }}
              onClick={() => navigate('/report')}
            >
              <span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: PALETTE.bg }}>
                  Your signal report
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'rgba(248,246,242,0.62)', fontWeight: 300, marginTop: 3 }}>
                  Review it, then share it if you choose
                </span>
              </span>
              <Chevron color={PALETTE.primary} />
            </button>

            {/* History */}
            <button
              className="sig-fu sig-d5"
              onClick={() => navigate('/history')}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginTop: 18,
                padding: '8px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                color: PALETTE.primary,
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: '0.02em',
              }}
            >
              View signal history
              <span aria-hidden>→</span>
            </button>
          </div>
        </div>

        <BottomNav />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Radial dial — 270° gold arc on a dim warm track, gap at bottom.    */
/* ------------------------------------------------------------------ */
function Dial({ score, calibrating, goldDash, delta, stateLabel, daysInApp, loading }) {
  const deltaText =
    delta == null
      ? null
      : delta > 0
      ? `▲ ${delta} from yesterday`
      : delta < 0
      ? `▼ ${Math.abs(delta)} from yesterday`
      : 'Holding steady';
  const deltaColor =
    delta == null ? PALETTE.textMute : delta < 0 ? PALETTE.textMid : PALETTE.primary;

  return (
    <div style={{ position: 'relative', width: 220, height: 220 }}>
      <svg width="220" height="220" viewBox="0 0 200 200">
        {/* dim warm track */}
        <circle
          cx="100"
          cy="100"
          r={DIAL_R}
          fill="none"
          stroke={PALETTE.surfaceAlt}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={TRACK_DASH}
          transform="rotate(135 100 100)"
        />
        {/* gold progress arc */}
        {!calibrating && (
          <circle
            cx="100"
            cy="100"
            r={DIAL_R}
            fill="none"
            stroke={PALETTE.primary}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={goldDash}
            transform="rotate(135 100 100)"
            style={{ transition: 'stroke-dasharray 0.8s ease' }}
          />
        )}
      </svg>

      {/* center content */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        {calibrating ? (
          <>
            <div
              className="serif"
              style={{ fontSize: 40, fontStyle: 'italic', color: PALETTE.textMute, lineHeight: 1 }}
            >
              —
            </div>
            <div style={{ fontSize: 11, color: PALETTE.textMid, fontWeight: 300, marginTop: 8, maxWidth: 120 }}>
              {Math.max(1, 7 - daysInApp)} more day{Math.max(1, 7 - daysInApp) !== 1 ? 's' : ''} to form
            </div>
          </>
        ) : (
          <>
            <div
              className="serif"
              style={{
                fontSize: 60,
                fontWeight: 400,
                fontStyle: 'italic',
                color: PALETTE.text,
                lineHeight: 1,
                letterSpacing: '-0.02em',
              }}
            >
              {loading ? '' : score}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                marginTop: 8,
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke={PALETTE.primary}
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={WAVEFORM} />
              </svg>
              <span
                style={{
                  fontSize: 10,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: PALETTE.primaryMid,
                  fontWeight: 600,
                }}
              >
                Signal
              </span>
            </div>
          </>
        )}
      </div>

      {/* delta + state label below the dial (within the gap area) */}
      {!calibrating && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 6,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 11.5, color: deltaColor, fontWeight: 500 }}>{deltaText}</div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: PALETTE.textMute,
              fontWeight: 500,
              marginTop: 3,
            }}
          >
            {stateLabel}
          </div>
        </div>
      )}
    </div>
  );
}

function Chevron({ color = PALETTE.primary }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: '0 0 auto' }}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function ChatIcon({ color = PALETTE.primary }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: '0 0 auto' }}
    >
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}
